// ============================================================================
// SHAREDBANNERMANAGER.JS - VERSION: 2026.01.21.1000
// ============================================================================
// Admin component for managing shared/generic banners
// UPDATES: Added Reward banner type for watch-ad-to-recover feature
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Plus, Save, Library, ExternalLink, Edit2, X } from 'lucide-react';

const API_URL = 'https://api.traktag.com/api';

const BANNER_TYPES = [
    { key: 'desktop', label: '🖥️ Desktop', size: '728×90' },
    { key: 'mobile', label: '📱 Mobile', size: '320×50' },
    { key: 'static', label: '📺 Static', size: '300×250' },
    { key: 'reward', label: '🎁 Reward', size: '300×250' },
    { key: 'exit', label: '🚪 Exit', size: '300×600' },
    { key: 'interstitial', label: '🎬 Interstitial', size: '320×480' }
];

const SharedBannerManager = () => {
    const [sponsors, setSponsors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newSponsorName, setNewSponsorName] = useState('');
    const [newAffiliateUrl, setNewAffiliateUrl] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [uploading, setUploading] = useState({});
    const [deleting, setDeleting] = useState({});
    const [editingAffiliate, setEditingAffiliate] = useState(null);
    const [editingName, setEditingName] = useState(null);
    const [deletingSponsor, setDeletingSponsor] = useState(null);
    const [error, setError] = useState(null);
    
    const fileInputRefs = useRef({});

    useEffect(() => {
        loadSponsors();
    }, []);

    const loadSponsors = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('admin_token');
            const response = await fetch(`${API_URL}/admin/banners/shared/list`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const data = await response.json();
            setSponsors(data.sponsors || []);
        } catch (err) {
            console.error('Failed to load sponsors:', err);
            setError(`Failed to load sponsors: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (sponsorSlug, bannerType, file, uploadKey) => {
        if (!file) return;

        setUploading(prev => ({ ...prev, [uploadKey]: true }));
        setError(null);

        try {
            const formData = new FormData();
            formData.append('banner', file);
            formData.append('sponsor', sponsorSlug);
            formData.append('bannerType', bannerType);

            const token = localStorage.getItem('admin_token');
            const response = await fetch(`${API_URL}/admin/banners/shared/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            const result = await response.json();
            if (result.success) {
                loadSponsors();
            } else {
                setError('Upload failed: ' + (result.error || 'Unknown error'));
            }
        } catch (err) {
            setError('Upload error: ' + err.message);
        } finally {
            setUploading(prev => ({ ...prev, [uploadKey]: false }));
            if (fileInputRefs.current[uploadKey]) {
                fileInputRefs.current[uploadKey].value = '';
            }
        }
    };

    const handleDeleteBanner = async (sponsorSlug, bannerType) => {
        if (!window.confirm(`Delete ${bannerType} banner for ${sponsorSlug}?`)) return;

        const deleteKey = `${sponsorSlug}-${bannerType}`;
        setDeleting(prev => ({ ...prev, [deleteKey]: true }));

        try {
            const token = localStorage.getItem('admin_token');
            const response = await fetch(`${API_URL}/admin/banners/shared/${sponsorSlug}/${bannerType}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const result = await response.json();
            if (result.success) {
                loadSponsors();
            } else {
                setError('Delete failed: ' + (result.error || 'Unknown error'));
            }
        } catch (err) {
            setError('Delete error: ' + err.message);
        } finally {
            setDeleting(prev => ({ ...prev, [deleteKey]: false }));
        }
    };

    const handleDeleteSponsor = async (sponsorSlug, displayName) => {
        if (!window.confirm(`Delete "${displayName}" and ALL its banners? This cannot be undone.`)) return;

        setDeletingSponsor(sponsorSlug);

        try {
            const token = localStorage.getItem('admin_token');
            const response = await fetch(`${API_URL}/admin/banners/shared/${sponsorSlug}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const result = await response.json();
            if (result.success) {
                loadSponsors();
            } else {
                setError('Delete sponsor failed: ' + (result.error || 'Unknown error'));
            }
        } catch (err) {
            setError('Delete sponsor error: ' + err.message);
        } finally {
            setDeletingSponsor(null);
        }
    };

    const handleAddSponsor = async () => {
        if (!newSponsorName.trim()) {
            setError('Please enter a sponsor name');
            return;
        }

        try {
            const token = localStorage.getItem('admin_token');
            const sponsorSlug = newSponsorName.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
            
            const response = await fetch(`${API_URL}/admin/banners/shared/${sponsorSlug}/affiliate`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ 
                    affiliateUrl: newAffiliateUrl,
                    displayName: newSponsorName
                })
            });

            const result = await response.json();
            if (result.success) {
                setNewSponsorName('');
                setNewAffiliateUrl('');
                setShowAddForm(false);
                loadSponsors();
            } else {
                setError('Failed to create sponsor: ' + (result.error || 'Unknown error'));
            }
        } catch (err) {
            setError('Create sponsor error: ' + err.message);
        }
    };

    const updateAffiliateUrl = async (sponsorSlug, newUrl) => {
        try {
            const token = localStorage.getItem('admin_token');
            const response = await fetch(`${API_URL}/admin/banners/shared/${sponsorSlug}/affiliate`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ affiliateUrl: newUrl })
            });

            const result = await response.json();
            if (result.success) {
                setEditingAffiliate(null);
                loadSponsors();
            } else {
                setError('Failed to update affiliate URL');
            }
        } catch (err) {
            setError('Update error: ' + err.message);
        }
    };

    const updateSponsorName = async (sponsorSlug, newName) => {
        try {
            const token = localStorage.getItem('admin_token');
            const response = await fetch(`${API_URL}/admin/banners/shared/${sponsorSlug}/affiliate`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ displayName: newName })
            });

            const result = await response.json();
            if (result.success) {
                setEditingName(null);
                loadSponsors();
            } else {
                setError('Failed to update sponsor name');
            }
        } catch (err) {
            setError('Update error: ' + err.message);
        }
    };

    return (
        <div className="bg-purple-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
                <h4 className="text-white font-bold text-lg flex items-center gap-2">
                    <Library size={20} /> 📦 Shared Banner Library
                </h4>
                <button
                    onClick={() => setShowAddForm(true)}
                    className="bg-teal-600 hover:bg-teal-500 text-white text-sm px-3 py-2 rounded flex items-center gap-1"
                >
                    <Plus size={16} /> Add Sponsor
                </button>
            </div>

            <p className="text-purple-300 text-sm mb-4">
                Upload banners here once, then apply them to multiple quizzes. Perfect for generic advertisers like Amazon, Google Ads, etc.
            </p>

            {/* Error Message */}
            {error && (
                <div className="bg-red-900 border border-red-600 text-red-200 px-4 py-2 rounded mb-4 flex justify-between items-center">
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className="text-red-300 hover:text-white">
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Add Sponsor Form */}
            {showAddForm && (
                <div className="bg-purple-900 rounded-lg p-4 mb-4">
                    <h5 className="text-white font-semibold mb-3">Add New Sponsor</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <input
                            type="text"
                            value={newSponsorName}
                            onChange={(e) => setNewSponsorName(e.target.value)}
                            placeholder="Sponsor Name (e.g., Amazon)"
                            className="bg-purple-700 text-white px-3 py-2 rounded text-sm"
                        />
                        <input
                            type="url"
                            value={newAffiliateUrl}
                            onChange={(e) => setNewAffiliateUrl(e.target.value)}
                            placeholder="Affiliate URL (optional)"
                            className="bg-purple-700 text-white px-3 py-2 rounded text-sm"
                        />
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={handleAddSponsor}
                            className="bg-teal-600 hover:bg-teal-500 text-white text-sm px-4 py-2 rounded"
                        >
                            Create Sponsor
                        </button>
                        <button
                            onClick={() => {
                                setShowAddForm(false);
                                setNewSponsorName('');
                                setNewAffiliateUrl('');
                            }}
                            className="bg-gray-600 hover:bg-gray-500 text-white text-sm px-4 py-2 rounded"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Loading */}
            {loading && (
                <p className="text-purple-400 text-center py-8">Loading sponsors...</p>
            )}

            {/* No Sponsors */}
            {!loading && sponsors.length === 0 && (
                <div className="text-center py-8 text-purple-400">
                    <p className="mb-2">No shared sponsors yet</p>
                    <p className="text-sm">Click "Add Sponsor" to create your first shared banner set</p>
                </div>
            )}

            {/* Sponsors List */}
            {!loading && sponsors.length > 0 && (
                <div className="space-y-4">
                    {sponsors.map(sponsor => (
                        <div key={sponsor.sponsor} className="bg-purple-900 rounded-lg p-4">
                            {/* Sponsor Header */}
                            <div className="flex items-center justify-between mb-2">
                                {editingName === sponsor.sponsor ? (
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            defaultValue={sponsor.displayName || sponsor.sponsor}
                                            id={`name-${sponsor.sponsor}`}
                                            className="bg-purple-700 text-white px-2 py-1 rounded text-sm font-bold"
                                        />
                                        <button
                                            onClick={() => {
                                                const input = document.getElementById(`name-${sponsor.sponsor}`);
                                                updateSponsorName(sponsor.sponsor, input.value);
                                            }}
                                            className="bg-green-600 hover:bg-green-500 text-white text-xs px-2 py-1 rounded"
                                        >
                                            <Save size={14} />
                                        </button>
                                        <button
                                            onClick={() => setEditingName(null)}
                                            className="bg-gray-600 hover:bg-gray-500 text-white text-xs px-2 py-1 rounded"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        <h5 className="text-white font-bold">{sponsor.displayName || sponsor.sponsor}</h5>
                                        <button
                                            onClick={() => setEditingName(sponsor.sponsor)}
                                            className="text-purple-400 hover:text-purple-300"
                                        >
                                            <Edit2 size={14} />
                                        </button>
                                    </div>
                                )}
                                <div className="flex items-center gap-2">
                                    <span className="text-purple-400 text-xs">Folder: _shared/{sponsor.sponsor}/</span>
                                    <button
                                        onClick={() => handleDeleteSponsor(sponsor.sponsor, sponsor.displayName || sponsor.sponsor)}
                                        disabled={deletingSponsor === sponsor.sponsor}
                                        className={`text-xs px-2 py-1 rounded ${
                                            deletingSponsor === sponsor.sponsor
                                                ? 'bg-gray-600 text-gray-400'
                                                : 'bg-red-600 hover:bg-red-500 text-white'
                                        }`}
                                    >
                                        {deletingSponsor === sponsor.sponsor ? '⏳' : <Trash2 size={14} />} Delete Sponsor
                                    </button>
                                </div>
                            </div>

                            {/* Affiliate URL */}
                            <div className="mb-3">
                                <label className="text-purple-300 text-xs block mb-1">
                                    🔗 Affiliate URL:
                                </label>
                                {editingAffiliate === sponsor.sponsor ? (
                                    <div className="flex gap-2">
                                        <input
                                            type="url"
                                            defaultValue={sponsor.affiliateUrl}
                                            id={`affiliate-${sponsor.sponsor}`}
                                            className="flex-1 bg-purple-700 text-white px-3 py-1 rounded text-sm"
                                        />
                                        <button
                                            onClick={() => {
                                                const input = document.getElementById(`affiliate-${sponsor.sponsor}`);
                                                updateAffiliateUrl(sponsor.sponsor, input.value);
                                            }}
                                            className="bg-green-600 hover:bg-green-500 text-white text-xs px-2 py-1 rounded"
                                        >
                                            <Save size={14} />
                                        </button>
                                        <button
                                            onClick={() => setEditingAffiliate(null)}
                                            className="bg-gray-600 hover:bg-gray-500 text-white text-xs px-2 py-1 rounded"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex gap-2 items-center">
                                        <span className="text-teal-400 text-sm truncate flex-1">
                                            {sponsor.affiliateUrl || '(not set)'}
                                        </span>
                                        <button
                                            onClick={() => setEditingAffiliate(sponsor.sponsor)}
                                            className="text-purple-400 hover:text-purple-300 text-xs"
                                        >
                                            Edit
                                        </button>
                                        {sponsor.affiliateUrl && (
                                            <a
                                                href={sponsor.affiliateUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-purple-400 hover:text-purple-300"
                                            >
                                                <ExternalLink size={14} />
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Banner Upload Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                                {BANNER_TYPES.map(type => {
                                    const uploadKey = `${sponsor.sponsor}-${type.key}`;
                                    const hasImage = sponsor.banners?.[type.key];
                                    const isUploading = uploading[uploadKey];
                                    const isDeleting = deleting[uploadKey];

                                    return (
                                        <div 
                                            key={type.key}
                                            className="bg-purple-800 rounded p-2"
                                        >
                                            <p className="text-purple-300 text-xs font-semibold mb-1">
                                                {type.label}
                                            </p>
                                            <p className="text-purple-500 text-xs mb-2">{type.size}</p>

                                            {hasImage ? (
                                                <div className="relative">
                                                    <img
                                                        src={sponsor.banners[type.key]}
                                                        alt={type.label}
                                                        className="w-full h-12 object-contain bg-purple-900 rounded"
                                                    />
                                                    <span className="absolute top-0 right-0 bg-green-600 text-white text-xs px-1 rounded-bl">
                                                        ✓
                                                    </span>
                                                </div>
                                            ) : (
                                                <div className="w-full h-12 bg-purple-700 rounded flex items-center justify-center">
                                                    <span className="text-purple-500 text-xs">Empty</span>
                                                </div>
                                            )}

                                            <input
                                                type="file"
                                                accept="image/*"
                                                ref={el => fileInputRefs.current[uploadKey] = el}
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        handleUpload(sponsor.sponsor, type.key, file, uploadKey);
                                                    }
                                                }}
                                                className="hidden"
                                            />
                                            
                                            <div className="flex gap-1 mt-1">
                                                <button
                                                    onClick={() => fileInputRefs.current[uploadKey]?.click()}
                                                    disabled={isUploading}
                                                    className={`flex-1 text-xs py-1 rounded flex items-center justify-center gap-1 ${
                                                        isUploading
                                                            ? 'bg-gray-600 text-gray-400'
                                                            : 'bg-purple-600 hover:bg-purple-500 text-white'
                                                    }`}
                                                >
                                                    {isUploading ? (
                                                        '⏳'
                                                    ) : (
                                                        <>
                                                            <Upload size={12} /> 
                                                            {hasImage ? 'Replace' : 'Upload'}
                                                        </>
                                                    )}
                                                </button>
                                                
                                                {hasImage && (
                                                    <button
                                                        onClick={() => handleDeleteBanner(sponsor.sponsor, type.key)}
                                                        disabled={isDeleting}
                                                        className={`text-xs py-1 px-2 rounded ${
                                                            isDeleting
                                                                ? 'bg-gray-600 text-gray-400'
                                                                : 'bg-red-600 hover:bg-red-500 text-white'
                                                        }`}
                                                        title="Delete banner"
                                                    >
                                                        {isDeleting ? '⏳' : <Trash2 size={12} />}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Refresh Button */}
            <button
                onClick={loadSponsors}
                className="text-purple-400 hover:text-purple-300 text-sm mt-4"
            >
                🔄 Refresh sponsor list
            </button>
        </div>
    );
};

export default SharedBannerManager;
